<template>
  <LayoutLogin>
    <template v-slot:content>
      <v-sheet rounded min-width="450px" color="white" elevation="2">
        <v-progress-linear
          height="6"
          :active="isLoading"
          :indeterminate="isLoading"
        />
        <div class="pa-11">
          <div class="d-flex justify-center mb-8 mt-6">
            <Logo type="big" />
          </div>

          <p class="body1 text-center blue--text mb-6" data-private>
            {{ registerTitle }}
          </p>

          <StepMarker
            class="mb-8"
            :label="$t('common.step', { step: currentStep })"
            :step="currentStep"
          />

          <RegisterFirstStep
            v-if="currentStep === STEPS.FIRST"
            showTermsOfService
            :account="account"
            :buttonText="$t(registerFirstStepButton)"
            :errors="errors"
            :isLoading="isLoading"
            @onSubmit="onSubmitFirstStep"
            @onPasswordUpdated="onPasswordUpdated"
            @onToggleTOCs="onToggleTOCs"
          />

          <AccessLocationView
            v-if="currentStep === STEPS.SECOND"
            :submitText="$t('common.button.go_to_home')"
            :isButtonDisabled="!hasCompleteLocation"
            :organizationId="access.organizationId"
            :location="location"
            @onLocationChange="onLocationChange"
            @onSubmit="onSubmitSecondStep"
            @onCheckboxChecked="onCheckboxCheckedSecondStep"
          />
        </div>
      </v-sheet>
    </template>
  </LayoutLogin>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'

import api from '@/api'
import { isOfficeEnvironment, sendDataToOffice } from '@/lib/office'

import mixin from '@/mixins/mixin.vue'
import authMixin from '@/mixins/authMixin.vue'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import Logo from '@/components/Core/Logo/Logo.vue'
import AccessLocationView from '@/components/Access/AccessLocationView.vue'
import RegisterFirstStep from './components/RegisterFirstStep.vue'
import StepMarker from './components/StepMarker.vue'

const STEPS = {
  FIRST: 1,
  SECOND: 2,
}

const SAME_PASSWORD_AS_TEMPORARY_ERROR =
  'password_cannot_be_the_same_as_the_temporary_password'

export default {
  components: {
    LayoutLogin,
    Logo,
    StepMarker,
    RegisterFirstStep,
    AccessLocationView,
  },

  mixins: [mixin, authMixin],

  data() {
    return {
      currentStep: STEPS.FIRST,
      account: {
        _id: null,
        email: null,
        displayname: null,
        familyname: null,
        password: null,
        position: null,
        organizationId: null,
      },
      errors: {
        termsOfService: null,
        password: null,
      },
      isLoading: false,
      localLocation: {
        site: null,
        tags: null,
      },
      STEPS,
    }
  },

  created() {
    this.getEmailFormURL()
    this.setAccountFromParams()
  },

  computed: {
    ...mapState({
      access: state => state.access,
    }),

    registerTitle() {
      if (this.currentStep === STEPS.FIRST) {
        return this.$t('auth.access.create_account')
      } else {
        return this.$t('auth.access.confirm_your_location')
      }
    },

    registerFirstStepButton() {
      return 'common.button.next'
    },

    hasCompleteLocation() {
      return Boolean(this.location.site)
    },

    location() {
      return {
        site:
          this.access?.account?.site?._id ??
          this.access?.account?.site ??
          this.localLocation.site,
        tags:
          this.localLocation.tags ??
          this.access?.tags ??
          this.access?.account?.tags,
      }
    },
  },

  methods: {
    ...mapActions({
      updateLocationAttributes: 'updateLocationAttributes',
    }),

    ...mapMutations({
      updateLocation: 'updateLocation',
    }),

    getEmailFormURL() {
      if (this.getRouteQuery().email) {
        this.account.email = window.decodeURIComponent(
          this.getRouteQuery().email
        )
        return this.account.email
      }

      return false
    },

    async onSubmitFirstStep() {
      this.isLoading = true
      try {
        if (!this.account.password) {
          this.isLoading = false
          return false
        }

        if (!this.account.tocsHasAccepted) {
          this.errors = {
            ...this.errors,
            termsOfService: this.$t(
              'auth.register.please_accept_terms_of_services'
            ),
          }
          this.isLoading = false
          return false
        }

        await api.account.register(this.account)
        return this.signInWithLocalAccount()
      } catch (err) {
        if (this.isPasswordError(err)) {
          this.errors = {
            ...this.errors,
            password: this.$t('common.users.different_password_error'),
          }

          return
        }

        this.showError(
          this.$t('error.auth.login_failed_please_try_again_later')
        )
      } finally {
        this.isLoading = false
      }
    },

    isPasswordError(error) {
      return (
        error?.response?.data?.tmessage === SAME_PASSWORD_AS_TEMPORARY_ERROR
      )
    },

    async onSubmitSecondStep() {
      this.isLoading = true
      try {
        const organizationId = this.account.organizationId
        const accountId = this.account._id

        await this.updateLocationAttributes({
          organizationId,
          accountId,
          location: this.location,
        })

        if (isOfficeEnvironment()) {
          return sendDataToOffice({
            account: this.access.account,
            accessToken: this.access.token,
          })
        }

        this.replaceRoute({ name: 'home' })
      } catch (err) {
        this.showError(this.$t('auth.access.error_on_signIn'))
      } finally {
        this.isLoading = false
      }
    },

    async signInWithLocalAccount() {
      try {
        const { data: reverSession } = await api.account.login({
          email: this.account.email,
          password: this.account.password,
        })

        this.$track('session.Start', this.$store, {
          process: 'passwordSubmitted',
          status: 'success',
        })

        if (isOfficeEnvironment() && this.hasCompleteLocation) {
          return sendDataToOffice({
            account: reverSession.account,
            accessToken: reverSession?.accessToken ?? reverSession?.token,
          })
        }

        await this.setLoginData(reverSession)

        this.currentStep = STEPS.SECOND
      } catch (err) {
        this.$track('session.Start', this.$store, {
          process: 'passwordSubmitted',
          status: 'fail',
        })
        this.showError(this.$t('auth.access.error_on_signIn'))
      } finally {
        this.isLoading = false
      }
    },

    onPasswordUpdated(password) {
      this.account.password = password
      this.errors = { ...this.errors, password: null }
    },

    handleTrackevents(attrs) {
      if (attrs?.site) {
        this.$track('user.Update', this.$store, {
          siteLocation: attrs.site,
        })
      }

      if (attrs?.tags?.length > 0) {
        this.$track('user.Update', this.$store, {
          siteTags: attrs.tags,
        })
      }
    },

    onLocationChange(location) {
      this.localLocation.site = location.site
      this.localLocation.tags = location.tags

      this.handleTrackevents(location)

      this.updateLocation(this.localLocation)
    },

    onToggleTOCs(value) {
      this.account = {
        ...this.account,
        tocsHasAccepted: value,
      }

      if (value) {
        this.errors = { ...this.errors, termsOfService: null }
      }
    },

    async onCheckboxCheckedSecondStep(values) {
      try {
        const organization = this.account.organizationId
        const accountId = this.account._id

        if (values?.send_me_email_notifications) {
          this.$track('user.Register', this.$store, {
            process: 'receiveEmailNotifications',
          })
        }

        const data = {
          receiveEmailNotifications: values.send_me_email_notifications,
        }

        return await api.account.update(organization, accountId, data)
      } catch (e) {
        this.showError(this.$t('auth.access.error_on_checkbox'))
      }
    },

    setAccountFromParams() {
      const user = this.$route.params.account
      this.account._id = user?._id
      this.account.displayname = user?.displayname
      this.account.familyname = user?.familyname
      this.account.position = user?.position
      this.account.organizationId = user?.organization?._id
    },
  },
}
</script>
